import { Routes } from '@angular/router';
import { LoginComponent } from '@features/identity/login.component';
import { memberGuard } from '@core/guards/member.guard';
import { termsGuard } from '@core/guards/terms.guard';
import { unsavedChangesGuard } from '@core/guards/unsaved-changes.guard';
import { minRoleGuard } from '@core/guards/min-role.guard';
import { taskDoAccessGuard } from '@core/guards/task-do-access.guard';
import { threadDeeplinkGuard } from '@core/guards/thread-deeplink.guard';
import { RoleLevel } from '@shared/models/role.enum';
import {
  CampaignBreadcrumbContext,
  TaskMgmtBreadcrumbContext,
  campaignBreadcrumbContextResolver,
  taskBreadcrumbContextResolver,
  taskMgmtBreadcrumbContextResolver,
} from '@core/resolvers/breadcrumb-context.resolvers';
import { BreadcrumbContext } from '@shared/models/breadcrumb.model';
import { mobileOnlyMatch, mobilePathsMatch, mobileRootToLoginRedirect } from '@core/guards/mobile-route.guard';
import { MOBILE_TAB_PRELOAD_FLAG } from '@core/routing/mobile-tab-preloader';
import { mobileManageProfileUnsavedGuard } from './mobile/pages/profile/mobile-manage-profile-unsaved.guard';

function mobileBack(
  fallbackUrl: string,
  options: {
    allowedReturnUrls?: readonly string[];
    allowedReturnUrlPrefixes?: readonly string[];
    primeParentHistory?: boolean;
  } = {},
) {
  return {
    mobileBack: {
      fallbackUrl,
      primeParentHistory: options.primeParentHistory ?? true,
      appBackBehavior: 'replace' as const,
      allowedReturnUrls: options.allowedReturnUrls,
      allowedReturnUrlPrefixes: options.allowedReturnUrlPrefixes,
    },
  };
}

export const routes: Routes = [
  // IMPORTANT: This mobile-only login route MUST come before the desktop
  // `{ path: 'login', redirectTo: '', pathMatch: 'full' }` redirect below.
  // Why: Ionic's IonRouterOutlet keys its view stack by the URL serialized
  // relative to the activated parent route. When the mobile login lived at
  // `path: ''` *and* the mobile tabs shell also lived at `path: ''`, both
  // produced URL key `/` for the outer outlet. After post-login
  // `navigateByUrl('/aufgaben')`, the outlet found the cached login view at
  // key `/` and reused it — URL flipped to `/aufgaben` while the visible
  // page stayed on the login screen. Hosting mobile login at a non-empty
  // path (`/login`) gives it a distinct view-stack key and lets the tabs
  // shell mount cleanly.
  {
    path: 'login',
    canMatch: [mobileOnlyMatch],
    data: {
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      // Never cache the login page: a stale, in-flight UI state (e.g.
      // spinning Google button) must not reappear after logout.
      reuseRoute: false,
    },
    loadComponent: () =>
      import('./mobile/auth/mobile-login-page.component').then(
        (m) => m.MobileLoginPageComponent,
      ),
  },
  {
    path: 'login',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'magic-login',
    canMatch: [mobileOnlyMatch],
    data: {
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      reuseRoute: false,
    },
    loadComponent: () =>
      import('./mobile/auth/mobile-magic-login-page.component').then(
        (m) => m.MobileMagicLoginPageComponent,
      ),
  },
  {
    path: 'magic-login',
    loadComponent: () =>
      import('@features/identity/magic-login.component').then((m) => m.MagicLoginComponent),
    data: {
      hideBreadcrumb: true,
      hideHeader: true,
      hideHeaderAuth: true,
      hideAppChrome: true,
      reuseRoute: false,
    },
  },
  {
    path: 'invitation',
    canMatch: [mobileOnlyMatch],
    data: { hideBreadcrumb: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/auth/mobile-invitation-page.component').then(
        (m) => m.MobileInvitationPageComponent,
      ),
  },
  {
    path: 'invitation',
    loadComponent: () =>
      import('@features/identity/invitation.component').then((m) => m.InvitationComponent),
    // canActivate: [minRoleGuard],
    data: { hideBreadcrumb: true, hideHeaderAuth: true },
    // data: { hideBreadcrumb: true, minRole: RoleLevel.Guest },
  },
  {
    path: 'registration',
    canMatch: [mobileOnlyMatch],
    data: { hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/auth/mobile-registration-page.component').then(
        (m) => m.MobileRegistrationPageComponent,
      ),
  },
  {
    path: 'registration',
    loadComponent: () =>
      import('@features/identity/registration.component').then((m) => m.RegistrationComponent),
    // canActivate: [minRoleGuard],
    data: { hideHeaderAuth: true },
    // data: { hideBreadcrumb: true, minRole: RoleLevel.Member },
  },
  {
    path: 'register-magic',
    canMatch: [mobileOnlyMatch],
    data: { hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/auth/mobile-register-magic-page.component').then(
        (m) => m.MobileRegisterMagicPageComponent,
      ),
  },
  {
    path: 'register-magic',
    loadComponent: () =>
      import('@features/identity/register-magic.component').then((m) => m.RegisterMagicComponent),
    // canActivate: [minRoleGuard],
    data: { hideHeaderAuth: true },
    // data: { hideBreadcrumb: true, minRole: RoleLevel.Member },
  },
  {
    path: 'register-social',
    canMatch: [mobileOnlyMatch],
    data: { hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/auth/mobile-register-social-page.component').then(
        (m) => m.MobileRegisterSocialPageComponent,
      ),
  },
  {
    path: 'register-social',
    loadComponent: () =>
      import('@features/identity/register-social.component').then((m) => m.RegisterSocialComponent),
    // canActivate: [minRoleGuard],
    data: { hideHeaderAuth: true },
    // data: { hideBreadcrumb: true, minRole: RoleLevel.Member },
  },
  {
    path: 'forbidden',
    canMatch: [mobileOnlyMatch],
    data: { hideBreadcrumb: true, hideHeaderAuth: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/pages/system/mobile-system-pages').then(
        (m) => m.MobileForbiddenPageComponent,
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('@features/identity/forbidden.component').then((m) => m.ForbiddenComponent),
    data: { hideBreadcrumb: true, hideHeaderAuth: true },
  },
  {
    path: 'members/:id',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      ...mobileBack('/mehr', {
        allowedReturnUrls: ['/mehr', '/diskussionen'],
        allowedReturnUrlPrefixes: ['/tasks/do/'],
      }),
    },
    loadComponent: () =>
      import('./mobile/pages/profile/mobile-public-profile-page.component').then(
        (m) => m.MobilePublicProfilePageComponent,
      ),
  },
  {
    path: 'members/:id',
    loadComponent: () =>
      import('@features/public/profile.component').then((m) => m.ProfileComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.breadcrumb.profile',
      breadcrumbFallback: 'Member profile',
    },
  },
  {
    path: 'person/:id',
    redirectTo: 'members/:id',
    pathMatch: 'full',
  },
  {
    path: 'onboarding',
    canMatch: [mobileOnlyMatch],
    canActivate: [memberGuard, termsGuard],
    data: { hideBreadcrumb: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/pages/onboarding/mobile-onboarding-page.component').then(
        (m) => m.MobileOnboardingPageComponent,
      ),
  },
  {
    path: 'onboarding',
    loadComponent: () =>
      import('@features/onboarding/onboarding.component').then((m) => m.OnboardingComponent),
    canActivate: [memberGuard, termsGuard],
    data: {
      breadcrumb: 'app.actions.goToOnboarding',
      breadcrumbFallback: 'Onboarding',
    },
  },
  {
    path: 'contact-onboarding',
    canMatch: [mobileOnlyMatch],
    canActivate: [memberGuard, termsGuard],
    data: {
      hideBreadcrumb: true,
      hideHeader: true,
      hideHeaderAuth: true,
      hideAppChrome: true,
      reuseRoute: false,
    },
    loadComponent: () =>
      import('./mobile/pages/onboarding/mobile-contact-onboarding-page.component').then(
        (m) => m.MobileContactOnboardingPageComponent,
      ),
  },
  {
    path: 'contact-onboarding',
    canActivate: [memberGuard, termsGuard],
    data: {
      hideBreadcrumb: true,
      hideHeader: true,
      hideHeaderAuth: true,
      hideAppChrome: true,
      reuseRoute: false,
    },
    loadComponent: () =>
      import('./features/onboarding/contact-onboarding.component').then(
        (m) => m.ContactOnboardingComponent,
      ),
  },
  {
    path: 'terms',
    canMatch: [mobileOnlyMatch],
    data: { hideBreadcrumb: true, hideHeaderAuth: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/pages/system/mobile-system-pages').then(
        (m) => m.MobileTermsPageComponent,
      ),
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('@features/legal/terms-acceptance/terms-acceptance.component').then((m) => m.TermsAcceptanceComponent),
    data: { hideBreadcrumb: true, hideHeaderAuth: true, hideAppChrome: true },
  },
  {
    path: 'error/login',
    canMatch: [mobileOnlyMatch],
    data: { hideBreadcrumb: true, hideHeaderAuth: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/pages/system/mobile-system-pages').then(
        (m) => m.MobileLoginErrorPageComponent,
      ),
  },
  {
    path: 'error/login',
    loadComponent: () =>
      import('@features/identity/error.component').then((m) => m.LoginErrorComponent),
    data: { hideBreadcrumb: true, hideHeaderAuth: true },
  },
  {
    path: 'error/notfound',
    canMatch: [mobileOnlyMatch],
    data: { hideBreadcrumb: true, hideHeaderAuth: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/pages/system/mobile-system-pages').then(
        (m) => m.MobileLoginNotFoundPageComponent,
      ),
  },
  {
    path: 'error/notfound',
    loadComponent: () =>
      import('@features/identity/notfound.component').then((m) => m.LoginNotFoundComponent),
    data: { hideBreadcrumb: true, hideHeaderAuth: true },
  },
  {
    path: 'diskussionen/new',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      ...mobileBack('/diskussionen', { allowedReturnUrls: ['/diskussionen'] }),
    },
    loadComponent: () =>
      import('./mobile/pages/discussions/mobile-thread-compose-page.component').then(
        (m) => m.MobileThreadComposePageComponent,
      ),
  },
  {
    path: 'diskussionen/:threadId',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      ...mobileBack('/diskussionen', { allowedReturnUrls: ['/diskussionen', '/notifications'] }),
    },
    loadComponent: () =>
      import('./mobile/pages/discussions/mobile-thread-detail-page.component').then(
        (m) => m.MobileThreadDetailPageComponent,
      ),
  },
  {
    path: '',
    canMatch: [
      mobilePathsMatch(
        'aufgaben',
        'diskussionen',
        'notifications',
        'mehr',
        'kanban',
      ),
    ],
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      [MOBILE_TAB_PRELOAD_FLAG]: true,
    },
    loadComponent: () =>
      import('./mobile/shell/mobile-tabs-shell.component').then(
        (m) => m.MobileTabsShellComponent,
      ),
    children: [
      {
        path: '',
        redirectTo: 'aufgaben',
        pathMatch: 'full',
      },
      {
        path: 'aufgaben',
        data: { [MOBILE_TAB_PRELOAD_FLAG]: true },
        loadComponent: () =>
          import('./mobile/pages/mobile-aufgaben-page.component').then(
            (m) => m.MobileAufgabenPageComponent,
          ),
      },
      {
        path: 'diskussionen',
        data: { [MOBILE_TAB_PRELOAD_FLAG]: true },
        loadComponent: () =>
          import('./mobile/pages/discussions/mobile-discussions-page.component').then(
            (m) => m.MobileDiscussionsPageComponent,
          ),
      },
      {
        path: 'notifications',
        data: { [MOBILE_TAB_PRELOAD_FLAG]: true },
        loadComponent: () =>
          import('./mobile/pages/mobile-notifications-page.component').then(
            (m) => m.MobileNotificationsPageComponent,
          ),
      },
      {
        path: 'mehr',
        data: { [MOBILE_TAB_PRELOAD_FLAG]: true },
        loadComponent: () =>
          import('./mobile/pages/mobile-mehr-page.component').then(
            (m) => m.MobileMehrPageComponent,
          ),
      },
      {
        path: 'kanban',
        canActivate: [minRoleGuard, termsGuard],
        data: { minRole: RoleLevel.PartyLeader, [MOBILE_TAB_PRELOAD_FLAG]: true },
        loadComponent: () =>
          import('./mobile/pages/kanban/mobile-kanban-page.component').then(
            (m) => m.MobileKanbanPageComponent,
          ),
      },
    ],
  },
  {
    path: 'mobile',
    children: [
      {
        path: 'app/aufgaben',
        redirectTo: '/aufgaben',
        pathMatch: 'full',
      },
      {
        path: 'app/diskussionen',
        redirectTo: '/diskussionen',
        pathMatch: 'full',
      },
      {
        path: 'app/dateiablage',
        redirectTo: '/dateiablage',
        pathMatch: 'full',
      },
      {
        path: 'app/profil',
        redirectTo: '/manage-profile',
        pathMatch: 'full',
      },
      {
        path: 'notifications',
        redirectTo: '/notifications',
        pathMatch: 'full',
      },
      {
        path: '',
        redirectTo: '/aufgaben',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'aufgaben',
    loadComponent: () =>
      import('@features/aufgaben/aufgaben-page.component').then((m) => m.AufgabenPageComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.nav.aufgaben',
      breadcrumbFallback: 'Aufgaben',
    },
  },
  {
    path: 'kanban-full',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.PartyLeader, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/kanban/mobile-kanban-page.component').then(
        (m) => m.MobileKanbanPageComponent,
      ),
  },
  {
    path: 'kanban',
    loadComponent: () =>
      import('@features/work/kanban-page.component').then((m) => m.KanbanPageComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.PartyLeader,
      breadcrumb: 'app.nav.kanban',
      breadcrumbFallback: 'Kanban Board',
    },
  },
  {
    path: 'dateiablage',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.Member, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/files/mobile-files-page.component').then(
        (m) => m.MobileFilesPageComponent,
      ),
  },
  {
    path: 'dateiablage',
    loadComponent: () =>
      import('@features/work/file-storage-page.component').then((m) => m.FileStoragePageComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.nav.dateiablage',
      breadcrumbFallback: 'Dateiablage',
    },
  },
  {
    path: 'diskussionen',
    loadComponent: () =>
      import('@features/work/discussions-page.component').then((m) => m.DiscussionsPageComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.nav.diskussionen',
      breadcrumbFallback: 'Diskussionen',
    },
  },
  {
    path: 'gantt',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.Member, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/gantt/mobile-gantt-page.component').then(
        (m) => m.MobileGanttPageComponent,
      ),
  },
  {
    path: 'gantt',
    loadComponent: () =>
      import('@features/gantt/gantt-page.component').then((m) => m.GanttPageComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.nav.gantt',
      breadcrumbFallback: 'Planung',
    },
  },
  {
    path: 'kampagnen',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.PartyLeader, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/campaigns/mobile-campaigns-page.component').then(
        (m) => m.MobileCampaignsPageComponent,
      ),
  },
  {
    path: 'kampagnen',
    loadComponent: () =>
      import('@features/campaign-mgmt/campaign-mgmt.component').then((m) => m.CampaignMgmtComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.PartyLeader,
      breadcrumb: 'app.nav.kampagnen',
      breadcrumbFallback: 'Kampagnen',
    },
  },
  {
    path: 'megafon',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.PartyAdmin, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/megafon/mobile-megafon-page.component').then(
        (m) => m.MobileMegafonPageComponent,
      ),
  },
  {
    path: 'megafon/:campaignId',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.PartyAdmin, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/megafon') },
    loadComponent: () =>
      import('./mobile/pages/megafon/mobile-megafon-detail-page.component').then(
        (m) => m.MobileMegafonDetailPageComponent,
      ),
  },
  {
    path: 'megafon',
    loadComponent: () =>
      import('@features/megafon/megafon-redirect.component').then((m) => m.MegafonRedirectComponent),
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.PartyLeader,
      hideBreadcrumb: true,
    },
  },
  {
    path: 'party-mgmt',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.PartyLeader, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/party-mgmt/mobile-party-mgmt-page.component').then(
        (m) => m.MobilePartyMgmtPageComponent,
      ),
  },
  {
    path: 'party-mgmt',
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.PartyLeader,
      breadcrumb: 'app.nav.partyMgmt',
      breadcrumbFallback: 'Party Management',
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@features/party-mgmt/party-mgmt.component').then((m) => m.PartyMgmtComponent),
      },
      {
        path: 'import',
        canMatch: [mobileOnlyMatch],
        loadComponent: () =>
          import('./mobile/pages/party-mgmt/mobile-mass-import-page.component').then(
            (m) => m.MobileMassImportPageComponent,
          ),
        data: { hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/party-mgmt') },
      },
      {
        path: 'import',
        loadComponent: () =>
          import('@features/party-mgmt/mass-import/mass-import-wizard.component').then((m) => m.MassImportWizardComponent),
        data: {
          breadcrumb: 'massImport.title',
          breadcrumbFallback: 'Mass Import',
        },
      },
    ],
  },
  {
    path: 'campaign-mgmt/:id',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      ...mobileBack('/kampagnen', {
        allowedReturnUrls: ['/kampagnen', '/gantt', '/notifications'],
      }),
    },
    loadComponent: () =>
      import('./mobile/pages/campaigns/mobile-campaign-steps-page.component').then(
        (m) => m.MobileCampaignStepsPageComponent,
      ),
  },
  {
    path: 'campaign-mgmt',
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.nav.kampagnen',
      breadcrumbFallback: 'Kampagnen',
    },
    canActivate: [minRoleGuard, termsGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@features/campaign-mgmt/campaign-mgmt.component').then((m) => m.CampaignMgmtComponent),
        data: { breadcrumb: null },
      },
      {
        path: ':id',
        loadComponent: () =>
          import('@features/campaign-mgmt/campaign-detail-redirect.component').then((m) => m.CampaignDetailRedirectComponent),
        canActivate: [minRoleGuard, termsGuard],
        resolve: { breadcrumbContext: campaignBreadcrumbContextResolver },
        data: {
          minRole: RoleLevel.Member,
          breadcrumb: (context: BreadcrumbContext) => {
            const ctx = context.data['breadcrumbContext'] as
              | CampaignBreadcrumbContext
              | null;
            const name = ctx?.campaign?.campaignName?.trim?.() ?? '';
            return name.length > 0
              ? name
              : String(context.params['id'] ?? 'Campaign');
          },
        },
      },
    ],
  },
  {
    path: 'task-mgmt/:id',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.Member,
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      ...mobileBack('/kampagnen', {
        allowedReturnUrls: ['/kampagnen', '/gantt'],
        allowedReturnUrlPrefixes: ['/campaign-mgmt/'],
      }),
    },
    loadComponent: () =>
      import('./mobile/pages/task-mgmt/mobile-task-mgmt-page.component').then(
        (m) => m.MobileTaskMgmtPageComponent,
      ),
  },
  {
    path: 'task-mgmt/:id',
    loadComponent: () =>
      import('@features/task-mgmt/task-mgmt.component').then((m) => m.TaskMgmtComponent),
    canActivate: [minRoleGuard, termsGuard],
    resolve: { breadcrumbContext: taskMgmtBreadcrumbContextResolver },
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.breadcrumb.taskMgmt',
      breadcrumbFallback: 'Task management',
      breadcrumbTrail: (context: BreadcrumbContext) => {
        const ctx = context.data['breadcrumbContext'] as
          | TaskMgmtBreadcrumbContext
          | null;
        if (!ctx) {
          return [];
        }

        return [
          {
            label: 'app.nav.kampagnen',
            labelFallback: 'Kampagnen',
            url: '/kampagnen',
            isClickable: true,
          },
          {
            label: ctx.campaign.campaignName,
            url: `/kampagnen?campaignId=${ctx.campaign.campaignId}`,
            isClickable: true,
          },
          {
            label: ctx.step.actionName,
            url: null,
            isClickable: false,
          },
          {
            label: 'app.breadcrumb.taskMgmt',
            labelFallback: 'Task management',
            url: null,
            isClickable: false,
          },
        ];
      },
    },
  },
  {
    path: 'tasks',
    data: { breadcrumb: 'app.breadcrumb.tasks', breadcrumbFallback: 'Tasks' },
    children: [
      {
        path: 'configure/:id',
        redirectTo: 'edit/:id',
        pathMatch: 'full',
      },
      {
        path: 'edit/:id',
        canMatch: [mobileOnlyMatch],
        canActivate: [minRoleGuard, termsGuard],
        canDeactivate: [unsavedChangesGuard],
        data: {
          minRole: RoleLevel.Member,
          hideBreadcrumb: true,
          hideHeader: true,
          hideAppChrome: true,
          noViewportRenav: true,
          ...mobileBack('/aufgaben', {
            allowedReturnUrls: ['/aufgaben', '/kanban', '/gantt'],
            allowedReturnUrlPrefixes: ['/tasks/do/', '/task-mgmt/'],
          }),
        },
        loadComponent: () =>
          import('./mobile/pages/task/mobile-task-edit-page.component').then(
            (m) => m.MobileTaskEditPageComponent,
          ),
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import('@features/tasks/pages/task-capture-page/task-capture-page.component').then((m) => m.TaskCapturePageComponent),
        canActivate: [minRoleGuard, termsGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { breadcrumbContext: taskBreadcrumbContextResolver },
        data: {
          minRole: RoleLevel.Member,
          breadcrumb: 'app.breadcrumb.editTask',
          breadcrumbFallback: 'Edit Task',
          noViewportRenav: true,
        },
      },
      {
        path: 'do/:id',
        canMatch: [mobileOnlyMatch],
        canActivate: [taskDoAccessGuard, termsGuard],
        data: {
          minRole: RoleLevel.Member,
          hideBreadcrumb: true,
          hideHeader: true,
          hideAppChrome: true,
          ...mobileBack('/aufgaben', {
            allowedReturnUrls: ['/aufgaben', '/kanban', '/gantt', '/kampagnen'],
            allowedReturnUrlPrefixes: ['/task-mgmt/'],
          }),
        },
        loadComponent: () =>
          import('./mobile/pages/task/mobile-task-doing-page.component').then(
            (m) => m.MobileTaskDoingPageComponent,
          ),
      },
      {
        path: 'do/:id',
        loadComponent: () =>
          import('@features/tasks/pages/task-doing-page/task-doing-page.component').then((m) => m.TaskDoingPageComponent),
        canActivate: [taskDoAccessGuard, termsGuard],
        resolve: { breadcrumbContext: taskBreadcrumbContextResolver },
        data: {
          minRole: RoleLevel.Member,
          breadcrumb: 'app.breadcrumb.doTask',
          breadcrumbFallback: 'Do Task',
        },
      },
      {
        path: 'private/new',
        canMatch: [mobileOnlyMatch],
        canActivate: [minRoleGuard, termsGuard],
        data: {
          minRole: RoleLevel.Member,
          hideBreadcrumb: true,
          hideHeader: true,
          hideAppChrome: true,
          ...mobileBack('/aufgaben', { allowedReturnUrls: ['/aufgaben', '/kanban'] }),
        },
        loadComponent: () =>
          import('./mobile/pages/task/mobile-private-task-page.component').then(
            (m) => m.MobilePrivateTaskPageComponent,
          ),
      },
      {
        path: 'private/:id',
        canMatch: [mobileOnlyMatch],
        canActivate: [minRoleGuard, termsGuard],
        data: {
          minRole: RoleLevel.Member,
          hideBreadcrumb: true,
          hideHeader: true,
          hideAppChrome: true,
          ...mobileBack('/aufgaben', { allowedReturnUrls: ['/aufgaben', '/kanban'] }),
        },
        loadComponent: () =>
          import('./mobile/pages/task/mobile-private-task-page.component').then(
            (m) => m.MobilePrivateTaskPageComponent,
          ),
      },
      {
        path: 'private/:id',
        loadComponent: () =>
          import('@features/tasks/pages/private-task-page/private-task-page.component').then((m) => m.PrivateTaskPageComponent),
        canActivate: [minRoleGuard, termsGuard],
        data: {
          minRole: RoleLevel.Member,
          breadcrumb: 'Private task',
          breadcrumbFallback: 'Private Task',
        },
      },
      // map-task route deprecated - use /tasks/do for all task types
    ],
  },
  {
    path: 'admin',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    data: { minRole: RoleLevel.GlobalAdmin, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true },
    loadChildren: () =>
      import('./mobile/pages/admin/mobile-admin-pages').then((m) => m.MOBILE_ADMIN_ROUTES),
  },
  {
    path: 'admin',
    canActivate: [minRoleGuard, termsGuard],
    data: {
      minRole: RoleLevel.GlobalAdmin,
      breadcrumb: 'app.nav.adminCenter',
      breadcrumbFallback: 'Admin Center',
    },
    loadChildren: () =>
      import('@features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
  },
  {
    path: 'manage-profile/auth-method',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    canDeactivate: [
      (component: { confirmLeaveIfNeeded?: () => Promise<boolean> }) =>
        component.confirmLeaveIfNeeded?.() ?? true,
    ],
    data: { minRole: RoleLevel.Member, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/manage-profile') },
    loadComponent: () =>
      import('./mobile/pages/profile/mobile-auth-method-page.component').then(
        (m) => m.MobileAuthMethodPageComponent,
      ),
  },
  {
    path: 'manage-profile',
    canMatch: [mobileOnlyMatch],
    canActivate: [minRoleGuard, termsGuard],
    canDeactivate: [mobileManageProfileUnsavedGuard],
    data: { minRole: RoleLevel.Member, hideBreadcrumb: true, hideHeader: true, hideAppChrome: true, ...mobileBack('/mehr') },
    loadComponent: () =>
      import('./mobile/pages/profile/mobile-manage-profile-page.component').then(
        (m) => m.MobileManageProfilePageComponent,
      ),
  },
  {
    path: 'manage-profile',
    loadComponent: () =>
      import('@features/manage-profile/manage-profile.component').then((m) => m.ManageProfileComponent),
    canActivate: [minRoleGuard, termsGuard],
    canDeactivate: [unsavedChangesGuard],
    data: {
      minRole: RoleLevel.Member,
      breadcrumb: 'app.actions.account',
      breadcrumbFallback: 'Account',
    },
  },
  {
    // Mobile root: send unauthenticated/landing visitors to the dedicated
    // `/login` URL so the IonRouterOutlet view-stack key for login
    // (`/login`) does not collide with the tabs-shell parent route key
    // (`/`). See the comment on the `path: 'login'` mobile route above.
    //
    // We can't use `redirectTo` here because Angular forbids combining it
    // with `canMatch`. The canActivate guard returns a UrlTree, achieving
    // the same redirect semantics; the loadComponent below is never
    // reached because activation aborts.
    path: '',
    canMatch: [mobilePathsMatch('')],
    canActivate: [mobileRootToLoginRedirect],
    data: { hideBreadcrumb: true, hideHeader: true, hideAppChrome: true },
    loadComponent: () =>
      import('./mobile/auth/mobile-login-page.component').then(
        (m) => m.MobileLoginPageComponent,
      ),
  },
  {
    path: '',
    component: LoginComponent,
    data: {
      hideBreadcrumb: true,
      hideHeader: true,
      hideAppChrome: true,
      // Never cache the desktop login screen (see /login route above).
      reuseRoute: false,
    },
  },
  {
    path: 'welcome',
    redirectTo: '/',
    pathMatch: 'full',
  },
  {
    // Deep link used by mention / thread-engagement notification emails for
    // non-task discussions (`URL_FRONTEND/threads/:threadId`). The guard
    // always resolves to a UrlTree (login, or the thread's real target); this
    // component is never actually rendered.
    path: 'threads/:threadId',
    canActivate: [threadDeeplinkGuard],
    component: LoginComponent,
    data: { hideBreadcrumb: true, hideHeader: true, hideAppChrome: true },
  },
  // ⚠️ MUST be last
  {
    path: '**',
    redirectTo: '/',
    pathMatch: 'full',
  },
];
